import type { ComponentType } from 'react';
import React from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

export interface EnhancedRoutingProps {
    navigate: NavigateFunction;
    params: Record<string, string>;
    location: ReturnType<typeof useLocation>;
}

/**
 * Higher Order Component that exposes useNavigate, useParams, and useLocation hooks
 * from react-router-dom to class components.
 *
 */
export function withEnhancedRouting<P extends EnhancedRoutingProps>(
    WrappedComponent: ComponentType<P>
): React.FC<Omit<P, keyof EnhancedRoutingProps>> {
    const displayName = `withEnhancedRouting(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

    const WithEnhancedRouting: React.FC<Omit<P, keyof EnhancedRoutingProps>> = (props) => {
        const navigate = useNavigate();
        const location = useLocation();
        const params = useParams();

        return <WrappedComponent {...(props as P)} navigate={navigate} params={params} location={location} />;
    };

    WithEnhancedRouting.displayName = displayName;

    return WithEnhancedRouting;
}
